import { CheckCircle, Briefcase, GraduationCap, Heart } from "lucide-react";

const About = () => {
  const credentials = [
    "ICF Level 1 Trained Coach",
    "PROSCI Change Management Certified",
    "PMP & Agile Project Management",
    "HR & Organizational Effectiveness Expert",
  ];

  const values = [
    {
      icon: Heart,
      title: "Transformation",
      description: "Creating lasting change through deep, meaningful coaching relationships.",
    },
    {
      icon: Briefcase,
      title: "Excellence",
      description: "Committed to the highest standards in coaching practice and outcomes.",
    },
    {
      icon: GraduationCap,
      title: "Growth",
      description: "Continuous learning and development for both coach and client.",
    },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 md:py-32 bg-secondary/30"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Image */}
          <div className="md:col-span-5 order-2 md:order-1">
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/5 rounded-2xl"></div>
              <img
                src="https://customer-assets.emergentagent.com/job_cohort-learn-hub/artifacts/l0cg2foc_Deepa%20Professional%20Headshot%20Punch.png"
                alt="Deepa - Leadership & Career Coach"
                className="relative rounded-2xl shadow-lg w-full object-cover aspect-[4/5] object-top"
                data-testid="about-image"
              />
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-7 order-1 md:order-2 space-y-8">
            <div>
              <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
                About Deepa
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
                Leadership & Career Coach
              </h2>
            </div>

            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Deepa has extensive experience in human resources, project management, and process 
                improvement within organisations. She is skilled in managing change and establishing 
                systems that align with strategic objectives.
              </p>
              <p>
                Her expertise in project management frameworks, such as PMP and Agile, enables her 
                to effectively oversee significant transformational projects. Furthermore, she holds 
                a PROSCI certification, which emphasises the importance of addressing the people side 
                of change.
              </p>
              <p>
                Recently, she has been interested in projects related to emotional and mental health 
                and is currently leading a significant project in this area for the Australian 
                Government (New South Wales Department of Education).
              </p>
              <p>
                As a trained coach working towards her ICF PCC accreditation, she is committed to 
                helping people achieve their personal and professional goals. She assists clients 
                in identifying and changing limiting habits and beliefs, equipping them with 
                effective tools to succeed.
              </p>
            </div>

            {/* Credentials */}
            <div className="space-y-3">
              <h3 className="font-semibold text-lg">Credentials & Certifications</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {credentials.map((credential, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 text-sm"
                    data-testid={`credential-${index}`}
                  >
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                    <span>{credential}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mission Statement */}
            <div className="p-6 bg-primary/5 rounded-xl border border-primary/10">
              <h3 className="font-semibold text-lg mb-2">Mission</h3>
              <p className="text-muted-foreground italic">
                "Building a transformative space and trusted partnership for mid-career professionals 
                to embrace health, confidence, and joy, unlocking leadership that transforms lives 
                and communities."
              </p>
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="mt-20 md:mt-32">
          <div className="text-center mb-12">
            <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
              Core Values
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
              What Drives My Work
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-testid={`value-card-${index}`}
                className="p-8 bg-background rounded-xl border border-border/50 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4">
                  <value.icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{value.title}</h3>
                <p className="text-muted-foreground">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
